import { FieldArray, Formik, useFormikContext } from "formik";
import React from "react";
import * as Yup from "yup";
import FormLabel from "../FormLabel";
import ComboSearch from "../ComboSearch";
import { Button } from "../ui/button";
import { createRequest, getInventory } from "src/api/inventory";
import CustomInput from "../CustomInput";
import { useDashboardContext } from "src/context/DashboardContext";
import CustomSelect from "../CustomSelect";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showSuccessToast } from "src/lib/utils";
import { usePortalContext } from "src/context/PortalContext";

const initialValues = {
	from_id: "",
	to_id: "",
	items: [],
};

const itemValidator = Yup.object().shape({
	product_id: Yup.string().required("Please select a product"),
	qty: Yup.number().moreThan(0, "Quantity must be greater than 0"),
});

const validationSchema = Yup.object().shape({
	from_id: Yup.string().required("From entity is required"),
	to_id: Yup.string().required("To entity is required"),
	items: Yup.array().of(itemValidator).min(1, "At least one item must be provided"),
});

const RequestProducts = () => {
	const { entities } = useDashboardContext();
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const options = entities?.map(({ id, name }) => ({ label: name, value: id }));

	const { mutate, isLoading } = useMutation(createRequest, {
		onSuccess: () => {
			showSuccessToast("Request created successfully");
			queryClient.invalidateQueries(["requests"]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleSubmit, setFieldValue }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<CustomSelect
						name="from_id"
						label="From store/warehouse"
						placeholder="Select store/warehouse"
						value={values.from_id}
						onChange={(value) => setFieldValue("from_id", value)}
						options={options}
					/>
					<CustomSelect
						name="to_id"
						label="To store/warehouse"
						placeholder="Select store/warehouse"
						value={values.to_id}
						onChange={(value) => setFieldValue("to_id", value)}
						options={options}
					/>
					<FieldArray
						name="items"
						render={({ push, remove, replace }) => (
							<div className="tw-gap-4 tw-grid">
								<AddProduct push={push} replace={replace} />
								<div className="tw-grid tw-divide-y tw-mt-5 tw-divide-[#333333]">
									{values.items.map((_, index) => (
										<RequestItem key={index} index={index} remove={() => remove(index)} />
									))}
								</div>
							</div>
						)}
					/>
					<Button disabled={isLoading} className="tw-h-fit tw-mt-3" variant="secondary">
						Make Request
					</Button>
				</form>
			)}
		</Formik>
	);
};

const AddProduct = ({ push, replace }) => {
	const { values } = useFormikContext();

	const items = values.items;

	const initialValues = {
		product_id: "",
		qty: 1,
		product: null,
	};
	const validationSchema = Yup.object().shape({
		product_id: Yup.string().required("Please select a product"),
		product: Yup.mixed().nonNullable(),
	});

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={(values, { resetForm }) => {
				const existingItem = items?.find(({ product_id }) => product_id === values.product_id);

				if (existingItem) {
					const itemIndex = items?.findIndex(({ product_id }) => product_id === values.product_id);
					replace(itemIndex, { ...existingItem, qty: existingItem.qty + 1 });
				} else {
					push(values);
				}
				resetForm();
			}}
		>
			{({ values, setFieldValue, submitForm }) => (
				<div className="tw-grid tw-grid-cols-[3fr,1fr] tw-gap-3">
					<FormLabel name="product_id" label="Select Product from inventory list">
						<ComboSearch
							value={values.product_id}
							onChange={(value, option) => {
								setFieldValue("product_id", value);
								setFieldValue("product", option);
							}}
							placeholder="Search product..."
							className="tw-justify-between tw-w-full"
							optionRenderer={({ name, sku }) => (
								<div className="flex gap-2">
									<p>
										{name} (SKU: {sku})
									</p>
								</div>
							)}
							queryKey="items"
							queryFN={getInventory}
							mapFN={(option) => ({
								label: option.name,
								value: String(option.id),
								info: option,
							})}
						/>
					</FormLabel>
					<FormLabel name="as" label="submit" labelClassName="!tw-text-white">
						<Button type="button" onClick={submitForm} className="tw-h-fit tw-w-full" variant="secondary">
							Add Item
						</Button>
					</FormLabel>
				</div>
			)}
		</Formik>
	);
};

const RequestItem = ({ index, remove }) => {
	const { values, handleChange } = useFormikContext();

	const item = values.items[index];

	return (
		<div className="tw-flex tw-items-center tw-gap-2 tw-py-3.5">
			<p className="tw-flex-1 tw-font-semibold tw-text-[#333333]">
				{item?.product?.name} <span className="tw-text-sm tw-font-normal">(SKU: {item?.product?.sku})</span>
			</p>
			<CustomInput
				className="!tw-py-1.5 tw-h-[auto] tw-max-w-[90px]"
				value={item.qty}
				name={`items.${index}.qty`}
				onChange={handleChange}
				type="number"
				inputMode="numeric"
			/>
			<Button type="button" onClick={remove} variant="destructive" className="!tw-py-1.5 !tw-px-3 !tw-rounded-full tw-text-xs">
				Delete
			</Button>
		</div>
	);
};

export default RequestProducts;
